import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAliceGPT from '../../../../Hooks/AliceGPT/useAliceGPT';
import * as AliceGPTEvents from '../../../../Context/AliceGPT/AliceGPTContextEvents';

const defaultLogoPreview = '';

const menuOptions = [
  {
    key: 'Top',
    label: 'Top Navigation',
    illustration: (
      <svg width="120" height="48" viewBox="0 0 120 48" fill="none">
        <rect x="1" y="1" width="118" height="46" rx="6" fill="#F5F7FA" stroke="#D1D5DB" strokeWidth="2"/>
        <rect x="12" y="12" width="96" height="8" rx="2" fill="#E5E7EB"/>
        <rect x="12" y="28" width="32" height="6" rx="2" fill="#E5E7EB"/>
        <rect x="48" y="28" width="40" height="6" rx="2" fill="#E5E7EB"/>
      </svg>
    )
  },
  {
    key: 'Left',
    label: 'Left Navigation',
    illustration: (
      <svg width="120" height="48" viewBox="0 0 120 48" fill="none">
        <rect x="1" y="1" width="118" height="46" rx="6" fill="#F5F7FA" stroke="#D1D5DB" strokeWidth="2"/>
        <rect x="12" y="12" width="16" height="24" rx="2" fill="#E5E7EB"/>
        <rect x="32" y="16" width="64" height="6" rx="2" fill="#E5E7EB"/>
        <rect x="32" y="28" width="40" height="6" rx="2" fill="#E5E7EB"/>
      </svg>
    )
  }
];

function PortalSettingsSection() {
  const { t } = useTranslation('portal/alicegpt_portal_settings', { useSuspense: false });
  const [state, dispatch] = useAliceGPT();
  const { menuOrientation, logoFile, logoPreview } = state.portalSettings;
  const fileInputRef = useRef();
  const [uploadStatus, setUploadStatus] = useState({ isUploading: false, isSuccess: false, fileName: '' });

  function handleMenuChange(key) {
    dispatch({ type: AliceGPTEvents.UPDATE_PORTAL_MENU_ORIENTATION, payload: key });
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (file) {
      // Check file size (2MB = 2 * 1024 * 1024 bytes)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        setUploadStatus({ 
          isUploading: false, 
          isSuccess: false, 
          fileName: file.name,
          error: t('portal_settings.portal_logo.drop_zone.file_size_error')
        });
        return;
      }

      setUploadStatus({ isUploading: true, isSuccess: false, fileName: file.name });
      
      const reader = new FileReader();
      reader.onload = (ev) => {
        dispatch({ type: AliceGPTEvents.UPDATE_PORTAL_LOGO, payload: { file, preview: ev.target.result } });
        setUploadStatus({ isUploading: false, isSuccess: true, fileName: file.name });
      };
      reader.readAsDataURL(file);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      // Check file size (2MB = 2 * 1024 * 1024 bytes)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        setUploadStatus({ 
          isUploading: false, 
          isSuccess: false, 
          fileName: file.name,
          error: t('portal_settings.portal_logo.drop_zone.file_size_error')
        });
        return;
      }

      setUploadStatus({ isUploading: true, isSuccess: false, fileName: file.name });
      
      // Add visual feedback for successful drop
      const dropZone = e.currentTarget;
      dropZone.style.borderColor = '#10B981';
      dropZone.style.backgroundColor = '#F0FDF4';
      
      const reader = new FileReader();
      reader.onload = (ev) => {
        dispatch({ type: AliceGPTEvents.UPDATE_PORTAL_LOGO, payload: { file, preview: ev.target.result } });
        setUploadStatus({ isUploading: false, isSuccess: true, fileName: file.name });
        
        // Reset visual feedback after a short delay
        setTimeout(() => {
          dropZone.style.borderColor = '#D1D5DB';
          dropZone.style.backgroundColor = '#fff';
        }, 1000);
      };
      reader.readAsDataURL(file);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
    // Add visual feedback when dragging over the drop zone
    const dropZone = e.currentTarget;
    dropZone.style.borderColor = '#3B82F6';
    dropZone.style.backgroundColor = '#EFF6FF';
  }

  function handleDragLeave(e) {
    e.preventDefault();
    // Reset visual feedback when dragging away from the drop zone
    const dropZone = e.currentTarget;
    dropZone.style.borderColor = '#D1D5DB';
    dropZone.style.backgroundColor = '#fff';
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h4>{t('portal_settings.title')}</h4>
      <div style={{ background: '#F7F9FB', borderRadius: 12, padding: '10px 10px', marginBottom: 32, border: '1px solid #E5E7EB' }}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>{t('portal_settings.menu_orientation.title')}</div>
        <div style={{ color: '#6B7280', marginBottom: 24 }}>{t('portal_settings.menu_orientation.description')}</div>
        <div style={{ display: 'flex', gap: 24 }}>
          {menuOptions.map(opt => (
            <div
              key={opt.key}
              onClick={() => handleMenuChange(opt.key)}
              style={{
                border: menuOrientation === opt.key ? '2px solid #111827' : '1px solid #D1D5DB',
                borderRadius: 8,
                padding: '10px 10px',
                cursor: 'pointer',
                background: '#fff',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: menuOrientation === opt.key ? '0 0 0 2px #E5E7EB' : 'none'
              }}
            >
              {opt.illustration}
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="radio"
                  checked={menuOrientation === opt.key}
                  onChange={() => handleMenuChange(opt.key)}
                  style={{ accentColor: '#111827' }}
                />
                <span style={{ fontWeight: 500 }}>{t(`portal_settings.menu_orientation.options.${opt.key.toLowerCase()}.label`)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: '#F7F9FB', borderRadius: 12, padding: '10px 10px', border: '1px solid #E5E7EB' }}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>{t('portal_settings.portal_logo.title')}</div>
        <div style={{ color: '#6B7280', marginBottom: 24 }}>{t('portal_settings.portal_logo.description')}</div>
        
        <div style={{ display: 'flex', gap: 32 }}>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              border: '2px dashed #D1D5DB',
              borderRadius: 8,
              padding: 32,
              flex: 1,
              minWidth: 260,
              minHeight: 180,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
              cursor: 'pointer'
            }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <div style={{ fontSize: 32, color: '#9CA3AF', marginBottom: 8 }}>{t('portal_settings.portal_logo.drop_zone.icon')}</div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>{t('portal_settings.portal_logo.drop_zone.title')}</div>
            
            {/* File name display */}
            {uploadStatus.isSuccess && (
              <div style={{ 
                color: '#10B981', 
                fontWeight: 500, 
                fontSize: 14, 
                marginBottom: 8,
                textAlign: 'center'
              }}>
                {uploadStatus.fileName}
              </div>
            )}
            
            {/* Error message display */}
            {uploadStatus.error && (
              <div style={{ 
                color: '#DC2626', 
                fontWeight: 500, 
                fontSize: 14, 
                marginBottom: 8,
                textAlign: 'center'
              }}>
                {uploadStatus.error}
              </div>
            )}
            
            <button
              type="button"
              style={{
                border: '1px solid #D1D5DB',
                borderRadius: 4,
                padding: '6px 16px',
                background: '#F3F4F6',
                fontWeight: 500,
                cursor: 'pointer',
                marginBottom: 8
              }}
              onClick={e => {
                e.stopPropagation();
                fileInputRef.current && fileInputRef.current.click();
              }}
            >{t('portal_settings.portal_logo.drop_zone.choose_file')}</button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <div style={{ color: '#9CA3AF', fontSize: 12, marginTop: 8 }}>
              {t('portal_settings.portal_logo.drop_zone.supported_formats')} (Max 2MB)
            </div>
          </div>
          <div style={{ minWidth: 220 }}>
            <div style={{ fontWeight: 500, marginBottom: 8 }}>{t('portal_settings.portal_logo.preview.title')}</div>
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>{t('portal_settings.portal_logo.preview.preview_logo')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 32, height: 32, background: '#F3F4F6', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {logoPreview ? <img src={logoPreview} alt="logo" style={{ width: 24, height: 24, objectFit: 'contain' }} /> : <span style={{ color: '#9CA3AF', fontSize: 18 }}>🖼️</span>}
                </div>
                <div style={{ flex: 1, height: 8, background: '#E5E7EB', borderRadius: 2 }}></div>
                <div style={{ flex: 1, height: 8, background: '#E5E7EB', borderRadius: 2 }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default PortalSettingsSection; 